import { PublicClientApplication } from "@azure/msal-browser";
import { AxiosError } from "axios";
import { enqueueSnackbar } from "notistack";
import { useMemo } from "react";
import { type NavigateFunction, useNavigate } from "react-router-dom";
import type { ImmutableObject } from "@/lib/immutable";
import { storefrontSdk } from "@/storefrontsdk";
import { queryClient } from "../App";
import type { ModelStateDictionary } from "../data/ModelStateDictionary";
import { createStore, type Store, useStore } from "./store";
import { type AppConfigState, useAppConfigState } from "./useAppConfigState";
import { type LocaleState, useLocaleState } from "./useLocaleState";
import { type LoginFormState, useLoginFormState } from "./useLoginFormState";
import {
	type ResetPasswordFormState,
	useResetPasswordFormState,
} from "./useResetPasswordFormState";
import { type SessionState, useSessionState } from "./useSessionState";

export type LoginRequest = {
	Email: string;
	Password: string;
};

export type AzureADRequest = {
	Email: string;
	Token: string;
	Tenant: string;
	Audience: string;
	Issuer: string;
};

export type LoginResponse = {
	AuthToken?: string | null;
	FullName?: string;
};

export class SendResetPasswordEmailRequest {
	EmailTo: string = "";
	Language: string = "en";
}
export type SendResetPasswordEmailResponse = {
	Success: boolean;
	StatusMessage: string;
};

export type ResetPasswordFromEmailRequest = {
	Email: string;
	TemporaryPassword: string;
	NewPassword: string;
};

export class SiteLayoutModel {
	loginFormPopupVisible: boolean = false;
	removeloginFormPopupCloseButton: boolean = false;
	registerFormPopupVisible: boolean = false;
	resetPasswordFormPopupVisible: boolean = false;
	errorMessage?: string; // shows error dialog when defined
	snackbarMessages: string[] = []; // shows snackbar message when not empty
	message?: string; // shows popup message dialog when defined

	tooltipId?: string;
	tooltipMessage?: string;
	tooltipVariant?: TooltipVariant;
	tooltipPlace?: TooltipPlace;
	tooltipHideDelay?: number;
	tooltipTimeoutHandle?: number;
}

const initialState = new SiteLayoutModel();
const siteLayoutGlobalStore = createStore<SiteLayoutModel>(initialState);

export class SiteLayoutState {
	private siteLayoutState: Store<SiteLayoutModel>;
	private appConfigState: AppConfigState;
	private sessionState: SessionState;
	private loginFormState: LoginFormState;
	private resetPasswordFormState: ResetPasswordFormState;
	private localeState: LocaleState;
	private navigate: NavigateFunction;

	constructor(
		siteLayoutState: Store<SiteLayoutModel>,
		appConfigState: AppConfigState,
		sessionState: SessionState,
		loginFormState: LoginFormState,
		resetPasswordFormState: ResetPasswordFormState,
		localeState: LocaleState,
		navigate: NavigateFunction,
	) {
		this.siteLayoutState = siteLayoutState;
		this.appConfigState = appConfigState;
		this.sessionState = sessionState;
		this.loginFormState = loginFormState;
		this.resetPasswordFormState = resetPasswordFormState;
		this.localeState = localeState;
		this.navigate = navigate;
	}

	get(): ImmutableObject<SiteLayoutModel> {
		return this.siteLayoutState.getState();
	}

	private clearSessionState() {
		window.localStorage.setItem("portal.auth.isAuthenticated", "false");
		window.localStorage.removeItem("portal.auth.fullname");
		window.localStorage.removeItem("portal.auth.email");
		window.localStorage.removeItem("portal.auth.dealid");
		window.localStorage.removeItem("portal.auth.deal");
		window.localStorage.removeItem("portal.auth.hasportal");
		window.localStorage.removeItem("portal.auth.hasstorefront");
		this.sessionState.reset();
		this.siteLayoutState.setState((prev) => ({
			...prev,
			loginFormPopupVisible: false,
			removeloginFormPopupCloseButton: false,
		}));
	}

	private getCurrentRedirectTarget(): string {
		const { pathname, search, hash } = window.location;
		const storefrontIndex = pathname.indexOf("/storefront");
		const relativePath =
			storefrontIndex >= 0
				? pathname.slice(storefrontIndex + "/storefront".length) || "/"
				: pathname || "/";

		if (relativePath.startsWith("/login")) {
			return "/";
		}

		return `${relativePath}${search}${hash}`;
	}

	redirectToLogin(redirectTarget?: string) {
		const target = redirectTarget ?? this.getCurrentRedirectTarget();
		this.clearSessionState();
		this.navigate(`/login?redirect=${encodeURIComponent(target)}`, {
			replace: true,
		});
	}

	private setErrorMessage(errorMessage?: string) {
		this.siteLayoutState.setState((prev) => ({ ...prev, errorMessage }));
	}

	setLoginFormPopupVisible(isVisible: boolean, removeCloseButton?: boolean) {
		if (isVisible) {
			this.sessionState.reset();
		}
		this.loginFormState.reset();
		this.siteLayoutState.setState((prev) => ({
			...prev,
			loginFormPopupVisible: isVisible,
			removeloginFormPopupCloseButton: removeCloseButton ?? false,
		}));
	}

	setRegisterFormPopupVisible(isVisible: boolean) {
		this.siteLayoutState.setState((prev) => ({
			...prev,
			registerFormPopupVisible: isVisible,
		}));
	}

	setResetPasswordFormPopupVisible(isVisible: boolean) {
		if (isVisible) {
			this.setLoginFormPopupVisible(false);
		}
		this.resetPasswordFormState.reset();
		this.siteLayoutState.setState((prev) => ({
			...prev,
			resetPasswordFormPopupVisible: isVisible,
		}));
	}

	async loginAsync() {
		const loginFailedTxt = `${this.loginFormState.translate("Login failed")}.`;
		this.sessionState.reset();
		var request: LoginRequest = {
			Email: this.loginFormState.get().emailValue,
			Password: this.loginFormState.get().passwordValue,
		};
		try {
			this.loginFormState.setIsWaitingForLogin(true);
			this.loginFormState.setError("");
			this.setErrorMessage(undefined);
			const loginResponse = await storefrontSdk.loginAsync(request);
			if (loginResponse.status === 200) {
				this.sessionState.set({
					fullName: loginResponse.data.FullName ?? "Unknown User",
					email: request.Email,
					loggedIn: true,
					deal: loginResponse.data.Deal ?? "",
					dealid: loginResponse.data.DealId ?? "",
					hasPortal: loginResponse.data.HasPortal ?? false,
					hasStorefront: loginResponse.data.HasStorefront ?? false,
				});
				this.setLoginFormPopupVisible(false);
				window.localStorage.setItem("portal.auth.isAuthenticated", "true");
				window.localStorage.setItem(
					"portal.auth.fullname",
					loginResponse.data.FullName ?? "",
				);
				window.localStorage.setItem(
					"portal.auth.email",
					loginResponse.data.Email ?? "",
				);
				window.localStorage.setItem(
					"portal.auth.dealid",
					loginResponse.data.DealId ?? "",
				);
				window.localStorage.setItem(
					"portal.auth.deal",
					loginResponse.data.Deal ?? "",
				);
				window.localStorage.setItem(
					"portal.auth.hasportal",
					(loginResponse.data.HasPortal ?? "false").toString(),
				);
				window.localStorage.setItem(
					"portal.auth.hasstorefront",
					(loginResponse.data.HasStorefront ?? "false").toString(),
				);
				return true;
			} else {
				//this.loginFormState.setError(`${loginFailedTxt}`);
				this.showError(`${loginFailedTxt}`);
			}
		} catch (error) {
			this.loginFormState.setError(error);
			console.error(error);
		} finally {
			this.loginFormState.setIsWaitingForLogin(false);
		}
		return false;
	}

	async loginAzureAsync() {
		// clear the interaction status so it can't get stuck on this and return: BrowserAuthError: interaction_in_progress: See https://aka.ms/msal.js.errors#interaction_in_progress for details
		sessionStorage.removeItem("msal.interaction.status");

		queryClient.invalidateQueries();
		const AzureADSettings = this.appConfigState.get().appConfig?.AzureAD;

		var msalobj = new PublicClientApplication({
			auth: {
				clientId: AzureADSettings?.ClientId || "",
				authority: `${AzureADSettings?.Instance || ""}${AzureADSettings?.TenantId || ""}`,
				redirectUri: `${this.appConfigState.get().appConfig?.StorefrontAppUrl ?? `${this.appConfigState.get().appConfig?.BaseUrl ?? ""}/storefront`}/redirect`,
			},
		});
		await msalobj.initialize();
		var loginResponse = await msalobj.loginPopup();
		//var tokenResponse = await msalobj.acquireTokenSilent({
		//        scopes: ["User.ReadWrite.All"]
		//    });

		const loginFailedTxt = `${this.loginFormState.translate("Login failed")}.`;
		this.sessionState.reset();
		var request: AzureADRequest = {
			Email: loginResponse.account.username,
			Token: loginResponse.idToken,
			Tenant: (loginResponse.idTokenClaims as IDTokenClaims).tid || "",
			Audience: (loginResponse.idTokenClaims as IDTokenClaims).aud || "",
			Issuer: (loginResponse.idTokenClaims as IDTokenClaims).iss || "",
		};
		try {
			this.loginFormState.setIsWaitingForLogin(true);
			this.loginFormState.setError("");
			this.setErrorMessage(undefined);
			const loginResponse = await storefrontSdk.azureLoginAsync(request);
			if (loginResponse.status === 200) {
				this.sessionState.set({
					fullName: loginResponse.data.FullName ?? "Unknown User",
					email: request.Email,
					loggedIn: true,
					deal: loginResponse.data.Deal ?? "",
					dealid: loginResponse.data.DealId ?? "",
					hasPortal: loginResponse.data.HasPortal ?? false,
					hasStorefront: loginResponse.data.HasStorefront ?? false,
				});
				this.setLoginFormPopupVisible(false);
				window.localStorage.setItem("portal.auth.isAuthenticated", "true");
				window.localStorage.setItem(
					"portal.auth.fullname",
					loginResponse.data.FullName ?? "",
				);
				window.localStorage.setItem(
					"portal.auth.email",
					loginResponse.data.Email ?? "",
				);
				window.localStorage.setItem(
					"portal.auth.dealid",
					loginResponse.data.DealId ?? "",
				);
				window.localStorage.setItem(
					"portal.auth.deal",
					loginResponse.data.Deal ?? "",
				);
				window.localStorage.setItem(
					"portal.auth.hasportal",
					(loginResponse.data.HasPortal ?? "false").toString(),
				);
				window.localStorage.setItem(
					"portal.auth.hasstorefront",
					(loginResponse.data.HasStorefront ?? "false").toString(),
				);
				return true;
			} else {
				//this.loginFormState.setError(`${loginFailedTxt}`);
				this.showError(`${loginFailedTxt}`);
			}
		} catch (error) {
			this.loginFormState.setError(error);
			console.error(error);
		} finally {
			this.loginFormState.setIsWaitingForLogin(false);
		}
		return false;
	}

	async logoutAsync(withReload: boolean = true): Promise<void> {
		// expire the portal cookie to delete it
		try {
			await storefrontSdk.apiV1StorefrontLogoutPost();
		} catch (error) {
			console.error(error);
		}
		this.clearSessionState();
		if (withReload) {
			window.location.reload();
		}
	}

	async sendResetPasswordEmailAsync(type: "reset" | "change") {
		const sendResetPasswordEmailFailedTxt = `${this.loginFormState.translate(`Send ${type} password email failed`)}.`;
		if (this.sessionState.get().loggedIn) {
			this.resetPasswordFormState.setEmail(this.sessionState.get().email);
		} else {
			// reset session
			this.sessionState.reset();
		}
		var request = new SendResetPasswordEmailRequest();
		request.EmailTo = this.resetPasswordFormState.get().emailValue;
		request.Language = this.localeState.get().language;
		try {
			this.resetPasswordFormState.setIsWaitingForSendResetPasswordEmail(true);
			const resetPasswordResponse =
				type === "change"
					? await storefrontSdk.sendChangePasswordEmailAsync(request)
					: await storefrontSdk.sendResetPasswordEmailAsync(request);
			const responseData = resetPasswordResponse.data as
				| SendResetPasswordEmailResponse
				| undefined;
			if (resetPasswordResponse.status === 200) {
				if (
					responseData &&
					responseData.Success === false &&
					responseData.StatusMessage
				) {
					this.showError(responseData.StatusMessage);
				} else {
					this.setResetPasswordFormPopupVisible(false);
					const msgTxt = this.sessionState.get().loggedIn
						? this.resetPasswordFormState.translate(
								`A ${type} password email was sent to`,
							)
						: this.resetPasswordFormState.translate(
								`Reset password email was sent to`,
							);
					enqueueSnackbar(`${msgTxt}: ${request.EmailTo}`, {
						variant: "success",
						autoHideDuration: null,
					});
					return true;
				}
			} else {
				this.showError(`${sendResetPasswordEmailFailedTxt}`);
			}
		} catch (error) {
			this.showError(error);
		} finally {
			this.resetPasswordFormState.setIsWaitingForSendResetPasswordEmail(false);
		}
		return false;
	}

	async resetPasswordFromEmailAsync(
		type: "reset" | "change",
	): Promise<boolean> {
		let upperCaseType = "Reset";
		if (type === "change") {
			upperCaseType = "Change";
		}
		const resetPasswordFailed = this.loginFormState.translate(
			`${upperCaseType} password failed`,
		);
		try {
			const loginResponse = await storefrontSdk.resetPasswordFromEmailAsync({
				Email: this.resetPasswordFormState.get().emailValue,
				TemporaryPassword:
					this.resetPasswordFormState.get().temporaryPasswordValue,
				NewPassword: this.resetPasswordFormState.get().newPasswordValue,
			});
			if (loginResponse.status === 200) {
				this.sessionState.set({
					fullName: loginResponse.data.FullName ?? "Unknown User",
					email: this.resetPasswordFormState.get().emailValue,
					loggedIn: true,
					deal: loginResponse.data.Deal ?? "",
					dealid: loginResponse.data.DealId ?? "",
					hasPortal: loginResponse.data.HasPortal ?? false,
					hasStorefront: loginResponse.data.HasStorefront ?? false,
				});
				window.localStorage.setItem("portal.auth.isAuthenticated", "true");
				window.localStorage.setItem(
					"portal.auth.fullname",
					loginResponse.data.FullName ?? "",
				);
				window.localStorage.setItem(
					"portal.auth.email",
					loginResponse.data.Email ?? "",
				);
				window.localStorage.setItem(
					"portal.auth.dealid",
					loginResponse.data.DealId ?? "",
				);
				window.localStorage.setItem(
					"portal.auth.deal",
					loginResponse.data.Deal ?? "",
				);
				window.localStorage.setItem(
					"portal.auth.hasportal",
					(loginResponse.data.HasPortal ?? "false").toString(),
				);
				window.localStorage.setItem(
					"portal.auth.hasstorefront",
					(loginResponse.data.HasStorefront ?? "false").toString(),
				);
				enqueueSnackbar(
					this.resetPasswordFormState.translate(
						`${upperCaseType} password was sucessful`,
					),
					{
						variant: "success",
						autoHideDuration: null,
					},
				);
				this.navigate("/");
				return true;
			} else {
				this.showError(`${resetPasswordFailed}`);
			}
		} catch (error) {
			this.showError(error);
		}
		return false;
	}

	showMessage(message: string) {
		if (message) {
			this.siteLayoutState.setState((prev) => ({ ...prev, message }));
			return;
		}
	}

	showError(error: unknown) {
		const anErrorOccuredTxt = `${this.resetPasswordFormState.translate("An error occured")}.`;
		if (!error) {
			this.setErrorMessage(undefined);
			return;
		}
		if (typeof error === "string") {
			this.loginFormState.setError(`${error}`);
			this.setErrorMessage(error);
			console.error(error);
			return;
		} else if (error instanceof AxiosError) {
			if (error.response) {
				if (error.response?.data) {
					switch (error.response.status) {
						case 401:
							this.redirectToLogin();
							break;
						case 400:
							if (error.response.data instanceof Object) {
								let msg = "";
								for (const field in error.response
									.data as ModelStateDictionary) {
									if (msg) {
										msg += ",";
									}
									msg += `[${field}]`;
									msg += " ";
									for (
										let i = 0;
										i <
										(error.response.data as ModelStateDictionary)[field].length;
										i++
									) {
										msg += ": ";
										msg += (error.response.data as ModelStateDictionary)[field][
											i
										];
										msg += " ";
									}
								}
								//this.loginFormState.setError(`${error.response.statusText}\n${msg}`);
								this.setErrorMessage(`${error.response.statusText}\n${msg}`);
								console.error(error.response.data);
								return;
							}
							break;
						case 500:
							if (error.message && error.response.data.Message) {
								//this.loginFormState.setError(`${anErrorOccuredTxt}. ${error.response.data.Message}`);
								this.setErrorMessage(
									`${error.message}. ${error.response.data.Message}`,
								);
								console.error(
									error.response.data.Message,
									error.response.data.StackTrace,
								);
								return;
							} else {
								this.setErrorMessage(error.message);
								console.error(error.response.data);
								return;
							}
						case 503:
							if (
								typeof error.response.data === "string" &&
								error.response.data.length > 0
							) {
								this.setErrorMessage(error.response.data);
								console.error(error.response.data);
								return;
							} else {
								const errorText = "Service Unavailable";
								this.setErrorMessage(errorText);
								console.error(errorText);
								return;
							}
						default:
							this.setErrorMessage(error.message);
							console.error(error);
							return;
					}
				} else {
					switch (error.response.status) {
						case 401:
							this.redirectToLogin();
							break;
						default:
							this.setErrorMessage(error.message);
							console.error(error);
							break;
					}
					return;
				}
			}
			this.setErrorMessage(error.message);
			console.log(error);
			return;
		} else if (error instanceof Error) {
			this.setErrorMessage(error.message);
			console.error(error);
			return;
		}
		if (!this.siteLayoutState.getState().errorMessage) {
			this.setErrorMessage(anErrorOccuredTxt);
			console.error(error);
			return;
		}
	}

	hideError() {
		this.setErrorMessage(undefined);
	}

	// biome-ignore lint/suspicious/noExplicitAny: any is ok here
	async handleErrorAsync(error: AxiosError | any) {
		if (error?.response?.status) {
			const axiosError = error as AxiosError;
			switch (error.response.status) {
				case 401:
					enqueueSnackbar(`401: Please try logging in again.`, {
						variant: "warning",
						autoHideDuration: 15000,
					});
					this.redirectToLogin();
					break;
				case 403:
					enqueueSnackbar(`403: Please try logging in again.`, {
						variant: "warning",
						autoHideDuration: 15000,
					});
					this.redirectToLogin();
					break;
				case 400: {
					let message = "";
					if (axiosError.response) {
						const badRequestResponse = axiosError.response
							.data as BadRequestResponse;
						for (const key in badRequestResponse) {
							const property = badRequestResponse[key];
							for (let i = 0; i < property.length; i++) {
								message += ` ${property[i]}`;
							}
						}
					}
					if (!message) {
						message = "400: Bad Request.";
					}
					enqueueSnackbar(message, {
						variant: "error",
						autoHideDuration: 15000,
					});
					break;
				}
				default:
					if (error.message) {
						enqueueSnackbar(error.message, {
							variant: "error",
							autoHideDuration: 15000,
						});
					} else {
						enqueueSnackbar(JSON.stringify(error), {
							variant: "error",
							autoHideDuration: 15000,
						});
					}
					break;
			}

			return;
		} else {
			if (error.message) {
				enqueueSnackbar(error.message, {
					variant: "error",
					autoHideDuration: 15000,
				});
			} else {
				enqueueSnackbar(JSON.stringify(error), {
					variant: "error",
					autoHideDuration: 15000,
				});
			}
		}
		//this.showError(error);
	}

	// translated with https://www.deepl.com/translator
	translate(text: string): string {
		const language = this.localeState.get().language;
		switch (text) {
			case "OK":
				switch (language) {
					default:
						return text;
				}
			case "Reset Password":
				switch (language) {
					case "es":
						return "Restablecer contraseña";
					case "fr":
						return "Réinitialiser le mot de passe";
					default:
						return text;
				}
			case "Change Password":
				switch (language) {
					case "es":
						return "Cambiar contraseña";
					case "fr":
						return "Modifier le mot de passe";
					default:
						return text;
				}
			case "Register":
				switch (language) {
					case "es":
						return "Registrar";
					case "fr":
						return "Enregistrer";
					default:
						return text;
				}
			case "Theme":
				switch (language) {
					case "es":
						return "Tema";
					case "fr":
						return "Thème";
					default:
						return text;
				}
			case "Light":
				switch (language) {
					case "es":
						return "Luminoso";
					case "fr":
						return "Lumineux";
					default:
						return text;
				}
			case "Dark":
				switch (language) {
					case "es":
						return "Oscuro";
					case "fr":
						return "Sombre";
					default:
						return text;
				}
			case "Light Theme":
				switch (language) {
					case "es":
						return "Tema Luminoso";
					case "fr":
						return "Thème Lumineux";
					default:
						return text;
				}
			case "Dark Theme":
				switch (language) {
					case "es":
						return "Tema Oscuro";
					case "fr":
						return "Thème Sombre";
					default:
						return text;
				}
			case "Shopping Cart":
				switch (language) {
					case "es":
						return "Cesta de la compra";
					case "fr":
						return "Panier d'achat";
					default:
						return text;
				}
			case "User Account Menu":
				switch (language) {
					case "es":
						return "Menú de cuenta de usuario";
					case "fr":
						return "Menu du compte d'utilisateur";
					default:
						return text;
				}
			case "Logout":
				switch (language) {
					case "es":
						return "Cerrar sesión";
					case "fr":
						return "Se déconnecter";
					default:
						return text;
				}
			case "Login":
				switch (language) {
					case "es":
						return "Iniciar sesión";
					case "fr":
						return "Se connecter";
					default:
						return text;
				}
			default:
				return text;
		}
	}

	showTooltip(options: TooltipOptions) {
		enqueueSnackbar(options.message, {
			variant: options.variant ?? "default",
			autoHideDuration: options.hideDelay ?? 3000,
			anchorOrigin: {
				horizontal: "right",
				vertical: "bottom",
			},
		});
	}

	hideTooltip() {
		const tooltipTimeoutHandle =
			this.siteLayoutState.getState().tooltipTimeoutHandle;
		if (tooltipTimeoutHandle !== undefined) {
			clearTimeout(tooltipTimeoutHandle);
		}
		this.siteLayoutState.setState((prev) => ({
			...prev,
			tooltipId: undefined,
			tooltipMessage: undefined,
			tooltipVariant: undefined,
			tooltipPlace: undefined,
			tooltipHideDelay: undefined,
			tooltipTimeoutHandle: undefined,
		}));
	}
}

export type TooltipVariant =
	| "success"
	| "warning"
	| "error"
	| "info"
	| "default";
export type TooltipPlace =
	| "top"
	| "top-start"
	| "top-end"
	| "right"
	| "right-start"
	| "right-end"
	| "bottom"
	| "bottom-start"
	| "bottom-end"
	| "left"
	| "left-start"
	| "left-end";

export type TooltipOptions = {
	id?: string;
	message?: string;
	variant?: TooltipVariant;
	place?: TooltipPlace;
	hideDelay?: number;
};

export function useSiteLayoutState(): SiteLayoutState {
	const appConfigState = useAppConfigState();
	const sessionState = useSessionState();
	const loginFormState = useLoginFormState();
	const resetPasswordFormState = useResetPasswordFormState();
	const localeState = useLocaleState();
	const navigate = useNavigate();
	useStore(siteLayoutGlobalStore);
	return useMemo(
		() =>
			new SiteLayoutState(
				siteLayoutGlobalStore,
				appConfigState,
				sessionState,
				loginFormState,
				resetPasswordFormState,
				localeState,
				navigate,
			),
		[
			appConfigState,
			sessionState,
			loginFormState,
			resetPasswordFormState,
			localeState,
			navigate,
		],
	);
}

type BadRequestResponse = {
	[key: string]: string[];
};

export interface IDTokenClaims {
	aud?: string;
	iss?: string;
	iat?: number;
	nbf?: number;
	exp?: number;
	aio?: string;
	email?: string;
	name?: string;
	nonce?: string;
	oid?: string;
	preferred_username?: string;
	rh?: string;
	roles?: string[];
	sub?: string;
	tid?: string;
	uti?: string;
	ver?: string;
}
